import React, { useEffect } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../../stores/authStore';
import { usePermission } from '../../hooks/usePermission';
import { ROUTES, ROUTE_PERMISSIONS } from '../../config/routes';

interface ProtectedRouteProps {
  requiredRole?: string | string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredRole,
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, user, fetchUserInfo, _hasHydrated } = useAuthStore();
  const { canAccessRoute, isSuperAdmin } = usePermission();

  useEffect(() => {
    // 如果有token但没有用户信息，尝试获取用户信息
    if (isAuthenticated && !user) {
      fetchUserInfo().catch(() => {
        // 获取失败会在request拦截器中处理
      });
    }
  }, [isAuthenticated, user, fetchUserInfo]);

  // 等待 hydration 完成
  if (!_hasHydrated) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  // 加载中
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  // 未登录，重定向到登录页
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // 超级管理员可以访问所有路由
  if (isSuperAdmin) {
    return <Outlet />;
  }

  // 检查角色权限
  if (requiredRole && user) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!roles.includes(user.role)) {
      // 无权限，重定向到工作台
      return <Navigate to={ROUTES.DASHBOARD} replace />;
    }
  }

  // 检查路由权限
  // 对于需要 super_admin 权限的路由，非超管无法访问
  const routePermissions = ROUTE_PERMISSIONS[location.pathname];
  if (routePermissions && routePermissions.includes('super_admin')) {
    // 只有超管可以访问 super_admin 权限的路由
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  if (!canAccessRoute(location.pathname)) {
    // 无权限访问该路由，重定向到工作台
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <Outlet />;
};
